import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Empty, Form, Input, List, Modal, Space, Typography } from 'antd';
import { User, ShieldCheck, Trash2, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import InternalLayout from './InternalLayout';
import './AccountCenter.css';

const { Text } = Typography;

const AccountCenter = () => {
  const { user, logout, updateUser } = useAuth();
  const [activeSection, setActiveSection] = useState('profile');
  const [profile, setProfile] = useState({
    full_name: '',
    school: '',
    course: '',
    bio: ''
  });
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saving, setSaving] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinMessage, setJoinMessage] = useState({ type: '', text: '' });
  const [tasksLoading, setTasksLoading] = useState(false);
  const [classTasks, setClassTasks] = useState([]);

  useEffect(() => {
    if (user?.id || user?.email) {
      setProfile({
        full_name: user.full_name || '',
        school: user.school || '',
        course: user.course || '',
        bio: user.bio || ''
      });
    }
  }, [user?.id, user?.email, user?.full_name, user?.school, user?.course, user?.bio]);

  const fetchMyTasks = async () => {
    if (user?.role !== 'student') {
      return;
    }
    setTasksLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/classes/my-tasks', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassTasks(response.data || []);
    } catch (error) {
      // Task loading is non-blocking for account management.
    } finally {
      setTasksLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const handleJoinClass = async () => {
    if (!joinCode.trim()) {
      setJoinMessage({ type: 'error', text: 'Enter a class join code.' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post('/api/classes/join', { join_code: joinCode.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJoinMessage({ type: 'success', text: 'Joined class successfully.' });
      setJoinCode('');
      fetchMyTasks();
    } catch (error) {
      setJoinMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to join class.' });
    } finally {
      setSaving(false);
    }
  };

  const handleProfileChange = (event) => {
    setProfile({ ...profile, [event.target.name]: event.target.value });
  };

  const handlePasswordChange = (event) => {
    setPasswords({ ...passwords, [event.target.name]: event.target.value });
  };

  const saveProfile = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.put('/api/auth/me', profile, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      updateUser(response.data);
      setMessage({ type: 'success', text: 'Profile updated successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  };

  const updatePassword = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.put('/api/auth/password', passwords, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setPasswords({ current_password: '', new_password: '' });
      setMessage({ type: 'success', text: 'Password updated.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update password.' });
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = () => {
    Modal.confirm({
      title: 'Deactivate your account?',
      content: 'You can contact support to restore it.',
      okText: 'Deactivate',
      okButtonProps: { danger: true },
      async onOk() {
        setSaving(true);
        setMessage({ type: '', text: '' });
        try {
          await axios.delete('/api/auth/me', {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          });
          logout();
        } catch (error) {
          setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to deactivate account.' });
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const accountNavItems = [
    { key: 'profile', icon: <User size={16} />, label: 'Profile' },
    { key: 'security', icon: <ShieldCheck size={16} />, label: 'Security' },
  ];

  if (!user) {
    return (
      <InternalLayout
        title="Account Center"
        description="Please sign in to manage your account."
      >
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No signed-in account." />
        </Card>
      </InternalLayout>
    );
  }

  return (
    <InternalLayout
      title="Account Center"
      description="Manage your profile, security, and account settings."
      navItems={accountNavItems}
      activeKey={activeSection}
      onNavChange={setActiveSection}
    >
      <div className="account-workspace">
        {message.text && (
          <Alert
            className="account-message"
            type={message.type === 'success' ? 'success' : 'error'}
            showIcon
            message={message.text}
          />
        )}

        {activeSection === 'profile' && (
          <Space className="account-section-stack" direction="vertical" size={16}>
            <Card title={<Space><User size={18} />Profile</Space>}>
              <div className="account-profile-form">
                <Form layout="vertical" onFinish={saveProfile}>
                  <Form.Item label="Full name">
                    <Input name="full_name" value={profile.full_name} onChange={handleProfileChange} />
                  </Form.Item>
                  <Form.Item label="School">
                    <Input name="school" value={profile.school} onChange={handleProfileChange} />
                  </Form.Item>
                  <Form.Item label="Course">
                    <Input name="course" value={profile.course} onChange={handleProfileChange} />
                  </Form.Item>
                  <Form.Item label="Bio">
                    <Input.TextArea name="bio" value={profile.bio} onChange={handleProfileChange} rows={3} />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" loading={saving}>
                    Save profile
                  </Button>
                </Form>
              </div>
            </Card>

            {user?.role === 'student' && (
              <Card title={<Space><Users size={18} />Join a Class</Space>}>
                <Text type="secondary">Enter the join code your teacher gave you to join a class.</Text>
                <Form className="account-join-form" layout="inline" onFinish={handleJoinClass}>
                  <Form.Item>
                    <Input
                      value={joinCode}
                      onChange={(event) => {
                        setJoinCode(event.target.value);
                        if (joinMessage.text) setJoinMessage({ type: '', text: '' });
                      }}
                      placeholder="Class join code"
                      autoComplete="off"
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={saving}>
                      Join
                    </Button>
                  </Form.Item>
                </Form>
                {joinMessage.text && (
                  <Alert
                    className="account-inline-alert"
                    type={joinMessage.type === 'success' ? 'success' : 'error'}
                    showIcon
                    message={joinMessage.text}
                  />
                )}
              </Card>
            )}

            {user?.role === 'student' && (
              <Card title={<Space><Users size={18} />My Class Tasks</Space>}>
                <Text type="secondary">Only active tasks assigned by your classes appear here.</Text>
                <div className="account-tasks-list">
                  <List
                    loading={tasksLoading}
                    dataSource={classTasks}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No active tasks yet." /> }}
                    renderItem={(item) => (
                      <List.Item>
                        <List.Item.Meta
                          title={item.class_name}
                          description={item.teacher_name ? `Teacher: ${item.teacher_name}` : 'No teacher assigned'}
                        />
                        <Space className="account-task-actions" wrap>
                          {item.modules?.length ? (
                            item.modules.map((module) => (
                              <Button
                                key={module.module_id}
                                htmlType="button"
                                onClick={() => window.open(`/game/${module.module_id}`, '_blank')}
                              >
                                {module.title}
                              </Button>
                            ))
                          ) : (
                            <Text type="secondary">No active tasks yet.</Text>
                          )}
                        </Space>
                      </List.Item>
                    )}
                  />
                </div>
              </Card>
            )}
          </Space>
        )}

        {activeSection === 'security' && (
          <Card title={<Space><ShieldCheck size={18} />Security</Space>}>
            <Form className="account-security-form" layout="vertical" onFinish={updatePassword}>
              <Form.Item label="Current password">
                <Input.Password
                  name="current_password"
                  value={passwords.current_password}
                  onChange={handlePasswordChange}
                />
              </Form.Item>
              <Form.Item label="New password">
                <Input.Password
                  name="new_password"
                  value={passwords.new_password}
                  onChange={handlePasswordChange}
                />
              </Form.Item>
              <Space wrap>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Update password
                </Button>
                <Button danger loading={saving} icon={<Trash2 size={16} />} onClick={deleteAccount}>
                  Deactivate account
                </Button>
              </Space>
              <div className="account-note">Email: {user?.email || '-'}</div>
            </Form>
          </Card>
        )}
      </div>
    </InternalLayout>
  );
};

export default AccountCenter;
